/**
 * Log que cresce acompanha a última linha, como num terminal (consoleai#25060).
 *
 * - Enquanto a pessoa está no fim, cada conteúdo novo rola para o fim.
 * - Se ela rola para cima para ler, o log NÃO a puxa de volta.
 * - Voltar ao fim (a menos de `limiar` px) retoma o acompanhamento.
 *
 * Devolve um callback ref para o elemento que ROLA — num `ScrollArea` do
 * Mantine é o `viewportRef`, não o `ref` do componente; num `div` com
 * `overflow: auto` é o próprio `ref`. `conteudo` é o que muda quando chega
 * linha nova (o array de linhas, o texto, o tamanho): o hook rola depois do
 * render que o trouxe.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

export const LIMIAR_DO_FIM_PX = 24

export function estaNoFim(el: HTMLElement, limiar = LIMIAR_DO_FIM_PX): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= limiar
}

export function useSeguirOFim<T extends HTMLElement = HTMLDivElement>(
  conteudo: unknown,
  limiar = LIMIAR_DO_FIM_PX,
): (el: T | null) => void {
  const [el, setEl] = useState<T | null>(null)
  const noFim = useRef(true)

  useEffect(() => {
    if (!el) return
    const aoRolar = () => { noFim.current = estaNoFim(el, limiar) }
    el.addEventListener('scroll', aoRolar, { passive: true })
    return () => el.removeEventListener('scroll', aoRolar)
  }, [el, limiar])

  useLayoutEffect(() => {
    if (el && noFim.current) el.scrollTop = el.scrollHeight
  }, [el, conteudo])

  return useCallback((novo: T | null) => setEl(novo), [])
}
